import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import { LoadingService, NotificationService } from './errorService';

// Navigation state management
interface NavigationState {
  currentPath: string;
  previousPath: string;
  isNavigating: boolean;
  refreshOnNavigate: boolean;
}

interface PageCache {
  [path: string]: {
    data: any;
    timestamp: number;
    ttl: number; // time to live in milliseconds
  };
}

export interface WalkthroughContent {
  title: string;
  steps: string[];
}

export const getWalkthroughContent = (path: string): WalkthroughContent => {
  const normalized = path.split('?')[0].split('#')[0];

  if (normalized === '/' || normalized === '/home') {
    return {
      title: 'Walkthrough: Home',
      steps: [
        'Use the top navigation to explore Marketplace, Community, and Services.',
        'Sign in to access your Dashboard and Account features.',
        'Open the Dashboard to manage shops, products, orders, and analytics.',
      ],
    };
  }

  if (normalized === '/marketplace' || normalized.startsWith('/marketplace/')) {
    return {
      title: 'Walkthrough: Marketplace',
      steps: [
        'Search and filter products using the search bar and category filters.',
        'Open a product to view details, seller info, and images.',
        'Add items to your cart or wishlist, then proceed to checkout.',
      ],
    };
  }

  if (normalized === '/community' || normalized.startsWith('/community/')) {
    return {
      title: 'Walkthrough: Community',
      steps: [
        'Browse discussions and use search to find topics.',
        'Open a post to read, comment, and react.',
        'Create a discussion to ask questions or share tips.',
      ],
    };
  }

  if (normalized === '/logistics' || normalized.startsWith('/logistics/')) {
    return {
      title: 'Walkthrough: Logistics',
      steps: [
        'Search for logistics providers and compare services.',
        'Create a shipping request with pickup, destination, and schedule.',
        'Track shipments and view status updates as they progress.',
      ],
    };
  }

  if (normalized === '/account' || normalized.startsWith('/account/')) {
    return {
      title: 'Walkthrough: My Account',
      steps: [
        'Update your profile details and contact information.',
        'Manage saved addresses for deliveries and billing.',
        'Review your wishlist, cart, and order history.',
      ],
    };
  }

  if (normalized === '/dashboard' || normalized.startsWith('/dashboard')) {
    if (normalized === '/dashboard/marketplace') {
      return {
        title: 'Walkthrough: Dashboard Marketplace',
        steps: [
          'Search products and refine results using filters.',
          'Add products to cart or wishlist from the list or product page.',
          'Use My Shops / Manage Products to create listings as a seller.',
        ],
      };
    }

    if (normalized === '/dashboard/community') {
      return {
        title: 'Walkthrough: Dashboard Community',
        steps: [
          'Browse discussions and open posts to comment.',
          'Create discussions and engage with likes/reactions.',
          'Use the sidebar to navigate to related modules.',
        ],
      };
    }

    if (normalized === '/dashboard/analytics') {
      return {
        title: 'Walkthrough: Analytics',
        steps: [
          'View visits and engagement captured from real navigation.',
          'Check “online users” for realtime presence while the app is open.',
          'Use top pages and timeline to understand what users visit most.',
        ],
      };
    }

    if (normalized === '/dashboard/logistics') {
      return {
        title: 'Walkthrough: Dashboard Logistics',
        steps: [
          'Create and manage transport requests.',
          'Track shipments and review tracking updates.',
          'Use provider tools to verify and manage logistics operations.',
        ],
      };
    }

    return {
      title: 'Walkthrough: Dashboard',
      steps: [
        'Use the left sidebar to open modules like Marketplace, Community, and Analytics.',
        'Check notifications in the top bar for updates.',
        'Use the profile menu to access account settings and actions.',
      ],
    };
  }

  return {
    title: 'Walkthrough: This Page',
    steps: [
      'Use the main navigation to explore related sections.',
      'Check for empty-state guidance if you have no data yet.',
      'If an action fails, verify you are signed in and your database tables/RLS are applied.',
    ],
  };
};

export class NavigationService {
  private static state: NavigationState = {
    currentPath: '/',
    previousPath: '/',
    isNavigating: false,
    refreshOnNavigate: true
  };

  private static cache: PageCache = {};
  private static listeners: ((state: NavigationState) => void)[] = [];
  private static navigate: NavigateFunction | null = null;

  // Initialize navigation service
  static initialize(navigateFunction: NavigateFunction) {
    this.navigate = navigateFunction;
    this.state.currentPath = window.location.pathname;
  }

  // Update current path
  static updateCurrentPath(path: string) {
    if (this.state.currentPath !== path) {
      this.state.previousPath = this.state.currentPath;
      this.state.currentPath = path;
      this.notifyListeners();
    }
  }

  // Add state listener
  static addListener(callback: (state: NavigationState) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify listeners
  private static notifyListeners() {
    this.listeners.forEach(listener => listener({ ...this.state }));
  }

  // Navigate with proper loading and refresh handling
  static async navigateTo(
    path: string, 
    options: {
      replace?: boolean;
      state?: any;
      refresh?: boolean;
      showLoading?: boolean;
      preloadData?: () => Promise<any>;
    } = {}
  ): Promise<void> {
    if (!this.navigate) {
      console.error('Navigation service not initialized');
      return;
    }

    const { replace = false, state, refresh = true, showLoading = true, preloadData } = options;

    // Set navigation state
    this.state.isNavigating = true;
    this.state.previousPath = this.state.currentPath;
    this.notifyListeners();

    if (showLoading) {
      LoadingService.setLoading('navigation', true);
    }

    try {
      // Preload data if provided
      if (preloadData) {
        const data = await preloadData();
        this.cachePageData(path, data);
      }

      // Clear cache for the target page if refresh is requested
      if (refresh) {
        this.clearPageCache(path);
      }

      // Perform navigation
      this.navigate(path, { replace, state });
      
      // Update current path
      this.state.currentPath = path;
      
      // Small delay to ensure page loads properly
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (error) {
      console.error('Navigation error:', error);
      NotificationService.error('Navigation Error', 'Failed to navigate to the requested page');
    } finally {
      this.state.isNavigating = false;
      if (showLoading) {
        LoadingService.setLoading('navigation', false);
      }
      this.notifyListeners();
    }
  }

  // Navigate back with refresh
  static async goBack(refresh: boolean = true): Promise<void> {
    if (this.state.previousPath && this.state.previousPath !== this.state.currentPath) {
      await this.navigateTo(this.state.previousPath, { refresh });
    } else {
      window.history.back();
    }
  }

  // Refresh current page
  static async refreshCurrentPage(): Promise<void> {
    const currentPath = this.state.currentPath;
    this.clearPageCache(currentPath);
    
    // Force a re-render by navigating to the same path
    await this.navigateTo(currentPath, { replace: true, refresh: true });
  }

  // Cache management
  static cachePageData(path: string, data: any, ttl: number = 5 * 60 * 1000): void {
    this.cache[path] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  static getCachedPageData(path: string): any | null {
    const cached = this.cache[path];
    if (!cached) return null;

    // Check if cache is expired
    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[path];
      return null;
    }

    return cached.data;
  }

  static clearPageCache(path?: string): void {
    if (path) {
      delete this.cache[path];
    } else {
      this.cache = {};
    }
  }

  static clearExpiredCache(): void {
    const now = Date.now();
    Object.keys(this.cache).forEach(path => {
      const cached = this.cache[path];
      if (now - cached.timestamp > cached.ttl) {
        delete this.cache[path];
      }
    });
  }

  // Navigation guards
  static setNavigationGuard(guard: (to: string, from: string) => boolean | Promise<boolean>): void {
    // Implementation for navigation guards if needed
    // This could be used to prevent navigation under certain conditions
  }

  // Get current navigation state
  static getState(): NavigationState {
    return { ...this.state };
  }

  // Check if currently navigating
  static isNavigating(): boolean {
    return this.state.isNavigating;
  }

  // Get current path
  static getCurrentPath(): string {
    return this.state.currentPath;
  }

  // Get previous path
  static getPreviousPath(): string {
    return this.state.previousPath;
  }

  // Set refresh on navigate preference
  static setRefreshOnNavigate(refresh: boolean): void {
    this.state.refreshOnNavigate = refresh;
    this.notifyListeners();
  }

  // Page lifecycle management
  static onPageLoad(path: string, callback: () => void | Promise<void>): void {
    // Execute callback when page loads
    if (this.state.currentPath === path) {
      if (typeof callback === 'function') {
        const result = callback();
        if (result instanceof Promise) {
          result.catch(error => {
            console.error('Page load callback error:', error);
          });
        }
      }
    }
  }

  static onPageUnload(path: string, callback: () => void | Promise<void>): void {
    // Execute callback when leaving page
    if (this.state.previousPath === path) {
      if (typeof callback === 'function') {
        const result = callback();
        if (result instanceof Promise) {
          result.catch(error => {
            console.error('Page unload callback error:', error);
          });
        }
      }
    }
  }

  // Breadcrumb management
  static getBreadcrumbs(path: string): Array<{ label: string; path: string }> {
    const pathSegments = path.split('/').filter(segment => segment);
    const breadcrumbs: Array<{ label: string; path: string }> = [
      { label: 'Home', path: '/' }
    ];

    let currentPath = '';
    pathSegments.forEach(segment => {
      currentPath += `/${segment}`;
      breadcrumbs.push({
        label: this.formatBreadcrumbLabel(segment),
        path: currentPath
      });
    });

    return breadcrumbs;
  }

  private static formatBreadcrumbLabel(segment: string): string {
    // Convert URL segment to readable label
    return segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Route validation
  static isValidRoute(path: string): boolean {
    // Define valid routes for the application
    const validRoutes = [
      '/',
      '/login',
      '/register',
      '/dashboard',
      '/dashboard/analytics',
      '/dashboard/community',
      '/dashboard/logistics',
      '/dashboard/marketplace',
      '/dashboard/products',
      '/dashboard/orders',
      '/dashboard/my-shops',
      '/dashboard/my-farms',
      '/dashboard/workers',
      '/dashboard/import-export',
      '/dashboard/notifications',
      '/dashboard/subscriptions',
      '/dashboard/admin',
      '/dashboard/ai/market-predictions',
      '/dashboard/ai/pest-detection',
      '/dashboard/ai/recommendations',
      '/dashboard/iot',
      '/dashboard/livestock-tracking',
      '/dashboard/traceability',
      '/dashboard/insurance',
      '/dashboard/lending',
      '/dashboard/cold-chain',
      '/dashboard/drone-mapping',
      '/marketplace',
      '/community',
      '/logistics',
      '/about',
      '/contact',
      '/help'
    ];

    // Check exact match or dynamic routes
    return validRoutes.includes(path) || 
           validRoutes.some(route => {
             if (route.includes(':')) {
               const routePattern = route.replace(/:[^/]+/g, '[^/]+');
               return new RegExp(`^${routePattern}$`).test(path);
             }
             return false;
           });
  }

  // Error page navigation
  static navigateToErrorPage(errorCode: number = 404): void {
    this.navigateTo(`/error/${errorCode}`, { replace: true });
  }

  // Utility methods for common navigation patterns
  static async navigateToHome(): Promise<void> {
    await this.navigateTo('/', { refresh: true });
  }

  static async navigateToDashboard(): Promise<void> {
    await this.navigateTo('/dashboard', { refresh: true });
  }

  static async navigateToMarketplace(): Promise<void> {
    await this.navigateTo('/marketplace', { refresh: true });
  }

  static async navigateToLogistics(): Promise<void> {
    await this.navigateTo('/logistics', { refresh: true });
  }

  static async navigateToProfile(): Promise<void> {
    await this.navigateTo('/dashboard/profile', { refresh: true });
  }

  // Handle browser back/forward buttons
  static handlePopState(): void {
    const currentPath = window.location.pathname;
    if (currentPath !== this.state.currentPath) {
      this.state.previousPath = this.state.currentPath;
      this.state.currentPath = currentPath;
      this.notifyListeners();
      
      // Refresh the page data when using browser navigation
      if (this.state.refreshOnNavigate) {
        this.clearPageCache(currentPath);
      }
    }
  }
}

// Initialize popstate listener
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    NavigationService.handlePopState();
  });

  // Clear expired cache every 5 minutes
  setInterval(() => {
    NavigationService.clearExpiredCache();
  }, 5 * 60 * 1000);
}

// React hook for navigation service
export const useNavigationService = () => {
  const [navigationState, setNavigationState] = React.useState<NavigationState>(NavigationService.getState());

  React.useEffect(() => {
    const unsubscribe = NavigationService.addListener(setNavigationState);
    return unsubscribe;
  }, []);

  return {
    navigationState,
    navigateTo: NavigationService.navigateTo,
    goBack: NavigationService.goBack,
    refreshCurrentPage: NavigationService.refreshCurrentPage,
    isNavigating: NavigationService.isNavigating,
    getCurrentPath: NavigationService.getCurrentPath,
    getBreadcrumbs: NavigationService.getBreadcrumbs,
    navigateToHome: NavigationService.navigateToHome,
    navigateToDashboard: NavigationService.navigateToDashboard,
    navigateToMarketplace: NavigationService.navigateToMarketplace,
    navigateToLogistics: NavigationService.navigateToLogistics,
    navigateToProfile: NavigationService.navigateToProfile
  };
};
